import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import Box, { BoxProps } from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

export type EmtChipProps = any & {
  /**
   * Type of chip
   * @default default
   */
  type?: "default" | "counter" | "roundedNumber";
  /**
   * @default 0
   */
  counterValue?: number;
  nonce?: string;
  color?: "transparent" | string;
  /**
   * If true, displays a loading skeleton instead of the chip
   */
  isLoading?: boolean;
};

type CounterProps = BoxProps<any> & {
  color?: string;
};

const Counter = styled(Box)(({ theme }: CounterProps) => ({
  borderRadius: "100%",
  background: theme.palette.common.white,
  color: theme.palette.primary.main,
  fontSize: "11px",
  width: "18px",
  height: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const RoundedNumberChip = styled(Chip)(() => ({
  width: "19px",
  height: "19px",
  fontSize: "8px",
  justifyContent: "flex-end",
}));

export function EmtChip(props: EmtChipProps) {
  const {
    label,
    type = "default",
    counterValue = 0,
    isLoading,
    ...rest
  } = props;

  // Render Skeletons when loading
  if (isLoading) {
    if (type === "roundedNumber") {
      // Render a circular skeleton for rounded number chip
      return <Skeleton variant="circular" width={19} height={19} />;
    }
    if (type === "counter") {
      // Render a composite skeleton: one for the label and one for the counter
      return (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Skeleton variant="text" width={50} height={24} />
          <Skeleton variant="circular" width={18} height={18} />
        </Box>
      );
    }
    // Default skeleton for regular chip
    return (
      <Skeleton
        sx={{ borderRadius: "100vmax" }}
        variant="rectangular"
        width={80}
        height={28}
      />
    );
  }

  const CounterType = (
    <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
      {label}
      <Counter {...rest}>{counterValue}</Counter>
    </Box>
  );

  const definitiveLabel = type === "counter" ? CounterType : label;

  return type === "roundedNumber" ? (
    <RoundedNumberChip label={label} {...rest} />
  ) : (
    <Chip label={definitiveLabel} {...rest} />
  );
}
